/**
 * Phase C.1 + C.2 — Settings modal: role / subcategory / custom instructions.
 *
 * Opened from the header ⚙ button. The user picks a role (one of 11
 * options including non-finance), optionally a finance sub-category (only
 * visible when role = Finance), and types persistent custom instructions.
 * On Save, the profile is persisted to localStorage and the parent
 * receives the new value via onSave so it can ship the field on the next
 * chat request.
 *
 * Phase C.3 also gates its session-log toggle from this modal so the user
 * has one place for all per-device preferences.
 */
import React, { useEffect, useState } from 'react';
import { UserProfile, ROLES, FINANCE_SUBCATEGORIES, CUSTOM_INSTRUCTIONS_MAX_CHARS } from '../services/userProfile';

interface Props {
  open: boolean;
  initial: UserProfile;
  sessionLogEnabled: boolean;
  onSessionLogToggle: (enabled: boolean) => void;
  onSave: (profile: UserProfile) => void;
  onCancel: () => void;
}

export function SettingsModal({ open, initial, sessionLogEnabled, onSessionLogToggle, onSave, onCancel }: Props): JSX.Element | null {
  const [role, setRole] = useState<string>(initial.role ?? '');
  const [subcategory, setSubcategory] = useState<string>(initial.subcategory ?? '');
  const [customInstructions, setCustomInstructions] = useState<string>(initial.customInstructions ?? '');

  // Reset local state whenever the modal opens with fresh initial values
  // (avoids showing stale form values after a previous Save).
  useEffect(() => {
    if (open) {
      setRole(initial.role ?? '');
      setSubcategory(initial.subcategory ?? '');
      setCustomInstructions(initial.customInstructions ?? '');
    }
  }, [open, initial.role, initial.subcategory, initial.customInstructions]);

  if (!open) return null;

  const showSubcategory = role === 'Finance';
  const ciLen = customInstructions.length;
  const ciTooLong = ciLen > CUSTOM_INSTRUCTIONS_MAX_CHARS;

  return (
    <div style={s.backdrop} role="dialog" aria-modal="true" aria-labelledby="settings-title">
      <div style={s.card}>
        <div id="settings-title" style={s.title}>Settings</div>

        <div style={s.section}>
          <label htmlFor="settings-role" style={s.label}>Your role</label>
          <select
            id="settings-role"
            value={role}
            onChange={(e) => {
              const r = e.target.value;
              setRole(r);
              if (r !== 'Finance') setSubcategory('');
            }}
            style={s.select}
          >
            <option value="">(none — generic assistant)</option>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <div style={s.hint}>Picks a default mindset for the agent. Practice IQ is not finance-only; it works for sales, marketing, ops, HR, research, and more.</div>
        </div>

        {showSubcategory && (
          <div style={s.section}>
            <label htmlFor="settings-subcategory" style={s.label}>Finance subcategory <span style={{ color: '#888', fontWeight: 400 }}>(optional)</span></label>
            <select
              id="settings-subcategory"
              value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
              style={s.select}
            >
              <option value="">(none — general FP&A / management accounting)</option>
              {FINANCE_SUBCATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        <div style={s.section}>
          <label htmlFor="settings-custom" style={s.label}>Custom instructions <span style={{ color: '#888', fontWeight: 400 }}>(optional)</span></label>
          <textarea
            id="settings-custom"
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            rows={4}
            placeholder={'Examples:\n• Always show amounts in AUD.\n• Use AASB 1060 conventions.\n• Round to the nearest thousand.'}
            style={s.textarea}
            maxLength={CUSTOM_INSTRUCTIONS_MAX_CHARS + 100 /* allow a tiny overshoot for the warning */}
          />
          <div style={{
            ...s.hint,
            color: ciTooLong ? '#C00000' : '#888',
          }}>
            {ciLen}/{CUSTOM_INSTRUCTIONS_MAX_CHARS} characters. Applied to every chat turn.
          </div>
        </div>

        <div style={s.section}>
          <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={sessionLogEnabled}
              onChange={(e) => onSessionLogToggle(e.target.checked)}
            />
            Auto session log
          </label>
          <div style={s.hint}>When on, every Practice IQ change is appended to a "Practice IQ Log" sheet inside the workbook. Survives across sessions.</div>
        </div>

        <div style={s.actions}>
          <button type="button" style={s.cancelBtn} onClick={onCancel}>Cancel</button>
          <button
            type="button"
            style={{ ...s.confirmBtn, ...(ciTooLong ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
            disabled={ciTooLong}
            onClick={() => onSave({
              role: role || undefined,
              subcategory: subcategory || undefined,
              customInstructions: customInstructions.trim() || undefined,
            })}
          >Save</button>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100,
    padding: 16,
  },
  card: {
    background: '#fff',
    border: '1px solid #cbd5e0',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    maxWidth: 480,
    width: '100%',
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1F3864',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: '#1f1f1f',
  },
  select: {
    fontSize: 12,
    padding: '6px 8px',
    border: '1px solid #cbd5e0',
    borderRadius: 4,
    background: '#fff',
  },
  textarea: {
    fontSize: 12,
    padding: 8,
    border: '1px solid #cbd5e0',
    borderRadius: 4,
    background: '#fff',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  hint: {
    fontSize: 11,
    color: '#666',
    lineHeight: 1.4,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 4,
  },
  cancelBtn: {
    padding: '6px 14px',
    fontSize: 12,
    background: '#fff',
    border: '1px solid #cbd5e0',
    borderRadius: 4,
    cursor: 'pointer',
  },
  confirmBtn: {
    padding: '6px 14px',
    fontSize: 12,
    background: '#1F3864',
    color: '#fff',
    border: '1px solid #1F3864',
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 600,
  },
};
